import * as React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import {
  spacing,
  typography,
  colors,
} from "../../../../../../../assets/styles/tokens";
import { useSearchParams } from "react-router-dom";
import { AppBar, Fab } from "@mui/material";
import CAC from "./CAC/CAC";
import CPA from "./CPA/CPA";
import CVR from "./CVR/CVR";
import FTD from "./FTD/FTD";
import PV from "./PV/PV";
import RS from "./RS/RS";
import Profit from "./Profit/Profit";
import ClickImpression from "./ClickImpression/ClickImpression";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import TrendsDetails from "./TrendsDetails";
import TrendsMobileSortWrap from "./TrendsMobileSortWrap";
import {
  selectUserData,
  selectUserToken,
} from "../../../../../../../features/userSlice";
import { useDispatch, useSelector } from "react-redux";
import { useTrendsMobile } from "../../helpers/hooks/useTrendsMobile";
import { TrendsDataSetQueryIds } from "../../../helpers/funcs/trendsAllQueryIDs";
import dayjs from "dayjs";
import { TrendsDashboardEndPointLogicMobile } from "../../../../../../../logic/AssetManagement/TrendsMobile/TrendsDashboardEndPointLogicMobile";
import { setLoadingGeoTrafficData } from "../../../../../../../features/trendsSlice";
import CreateTasksDrawer from "../CreateTrendModules/CreateTrend";

const tabConfig = [
  { label: "CAC", value: "TrendsMobileCAC", trackerId: "691" },
  {
    label: "Clicks & Impressions",
    value: "TrendsMobileClicksAndImpressions",
    trackerId: "692",
  },
  { label: "CPA", value: "TrendsMobileCPA", trackerId: "693" },
  { label: "CVR", value: "TrendsMobileCVR", trackerId: "694" },
  { label: "FTD", value: "TrendsMobileFTD", trackerId: "695" },
  { label: "PV", value: "TrendsMobilePV", trackerId: "696" },
  { label: "PROFIT", value: "TrendsMobileProfit", trackerId: "697" },
  { label: "RS", value: "TrendsMobileRS", trackerId: "698" },
];

const TAB_COMPONENTS = [CAC, ClickImpression, CPA, CVR, FTD, PV, Profit, RS];

interface TabPanelProps {
  children?: React.ReactNode;
  activeIndex: number;
  index: number;
  visited: boolean;
}

// Mirrors BizLeaderMobileDashboardTabs pattern:
// - Never mounts until the tab is first visited (no API call on hidden tabs)
// - Once visited, stays mounted with display:none (no re-mount, no re-fetch on tab switch)
const TabPanel = React.memo(
  ({ children, activeIndex, index, visited }: TabPanelProps) => {
    if (!visited) return null;
    return (
      <Box sx={{ display: activeIndex === index ? "block" : "none", pt: 1 }}>
        {children}
      </Box>
    );
  },
);

export default function TrendsMobileTabs() {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const response = useSelector(selectUserData);
  const [searchParams, setSearchParams] = useSearchParams();
  const startDate = searchParams.get("startDateTM");
  const endDate = searchParams.get("endDateTM");
  const websiteSharedUids = searchParams.get("workspace");
  const dateType = searchParams.get("dateType") || "click_date";
  const dataSet = searchParams.get("dateType");
  const isClickQuery = dateType === "click_date";
  const dispatch = useDispatch();
  const loadingTrendsMobData = useSelector(
    (state: any) => state.trends?.loadingTrendsMobData,
  );

  const selectedDimensions = React.useMemo(() => {
    const uidList = websiteSharedUids?.split(",") ?? [];
    return (
      response?.data?.website_permissions?.flatMap((siteObj: any) => {
        const shared_uid = Object.keys(siteObj)[0];
        if (!uidList.includes(shared_uid)) return [];
        return (siteObj[shared_uid].dimensions ?? []).map(
          (dim: any) => dim.dimension_name,
        );
      }) ?? []
    );
  }, [response, websiteSharedUids]);

  const currentTabParam = searchParams.get("tabName");
  const initialIndex = tabConfig.findIndex(
    (tab) => tab.value === currentTabParam,
  );
  const activeIndex = initialIndex !== -1 ? initialIndex : 0;

  const [value, setValue] = React.useState(activeIndex);

  // Only visited tabs are mounted — prevents off-screen tabs from firing useEffects/API calls
  // On first load, only the currently active tab is pre-visited
  const workspaceId = websiteSharedUids;
  const [visitedTabs, setVisitedTabs] = React.useState<Set<number>>(
    () => new Set([activeIndex]),
  );

  // Reset visited tabs when workspace changes (same pattern as BizLeader)
  const prevWorkspaceRef = React.useRef(workspaceId);
  if (prevWorkspaceRef.current !== workspaceId) {
    prevWorkspaceRef.current = workspaceId;
    setVisitedTabs(new Set([value]));
  }

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    // Mark this tab as visited so it mounts for the first time
    setVisitedTabs((prev) => new Set(prev).add(newValue));
    const newTabValue = tabConfig[newValue].value;
    searchParams.set("tabName", newTabValue);
    setSearchParams(searchParams);
  };

  const params = React.useMemo(() => {
    const baseParams = {
      oauthTokenHeader,
      queryId: isClickQuery
        ? TrendsDataSetQueryIds.CLICK
        : TrendsDataSetQueryIds.ACTION,
      startDate: startDate || "",
      endDate: endDate || "",
      timeUnit: "day",
      type: "site",
      websiteSharedUids: websiteSharedUids ? websiteSharedUids.split(",") : [],
      dimensions: selectedDimensions || [],
    };
    if (isClickQuery) {
      return {
        ...baseParams,
        campaignId: "1",
        adgroupId: "1",
        matchtype: "all",
        kwId: "1",
        country: "all",
        trafficSource: "all",
      };
    }
    return baseParams;
    //eslint-disable-next-line
  }, [
    oauthTokenHeader,
    dateType,
    startDate,
    endDate,
    websiteSharedUids,
    selectedDimensions,
  ]);

  const isReady =
    startDate && endDate && websiteSharedUids && selectedDimensions.length > 0;

  useTrendsMobile({
    params: isReady ? params : null,
    autoFetch: !!isReady,
  });

  React.useEffect(() => {
    const currentParams = new URLSearchParams(searchParams);
    if (!currentParams.get("dateType")) {
      currentParams.set("dateType", "click_date");
      setSearchParams(currentParams, { replace: true });
    }
    // eslint-disable-next-line
  }, [searchParams]);

  React.useEffect(() => {
    const currentParams = new URLSearchParams(searchParams);
    const hasStart = currentParams.get("startDateTM");
    const hasEnd = currentParams.get("endDateTM");
    if (!hasStart || !hasEnd) {
      const end = dayjs();
      const start = dayjs().subtract(14, "day");
      currentParams.set("startDateTM", start.format("YYYY-MM-DD"));
      currentParams.set("endDateTM", end.format("YYYY-MM-DD"));
      setSearchParams(currentParams, { replace: true });
    }
    // eslint-disable-next-line
  }, [searchParams]);

  React.useEffect(() => {
    if (dataSet === "click_date") {
      const fetchData = async () => {
        try {
          dispatch(setLoadingGeoTrafficData(true));
          await Promise.all([
            TrendsDashboardEndPointLogicMobile.getGeosTrendsMobile(
              oauthTokenHeader,
            ),
            TrendsDashboardEndPointLogicMobile.getTrafficSourcesTrendsMobile(
              oauthTokenHeader,
            ),
          ]);
        } catch (error) {
          console.error("Error fetching trends mobile data:", error);
        } finally {
          dispatch(setLoadingGeoTrafficData(false));
        }
      };
      fetchData();
    }
    // eslint-disable-next-line
  }, [dataSet, oauthTokenHeader]);

  React.useEffect(() => {
    if (!isReady) return;
    const fetchAN = async () => {
      try {
        await TrendsDashboardEndPointLogicMobile.getActivityNotesTrendsMobile({
          oauthTokenHeader,
          websiteSharedUid: websiteSharedUids?.split(",") || [],
          fromDate: startDate || "",
          toDate: endDate || "",
        });
      } catch (error) {
        console.error(
          "Error fetching activity notes trends mobile data:",
          error,
        );
      }
    };
    fetchAN();
  }, [oauthTokenHeader, websiteSharedUids, startDate, endDate, isReady]);

  // Fetch user list
  React.useEffect(() => {
    if (!oauthTokenHeader) return;
    TrendsDashboardEndPointLogicMobile.getAllUserList(oauthTokenHeader);
  }, [oauthTokenHeader]);

  return (
    <>
      <Box sx={{ height: "93vh", overflowY: "auto" }}>
        <AppBar
          position="sticky"
          sx={{
            bgcolor: "background.paper",
          }}
        >
          <Tabs
            value={value}
            onChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTabs-scrollButtons": {
                color: colors.GrayDarkMode[700].$value,
              },
            }}
            TabIndicatorProps={{
              sx: {
                backgroundColor: colors.Brand[600].$value,
                height: spacing.Spacing.xxs.$value,
              },
            }}
          >
            {tabConfig.map((tab, index) => (
              <Tab
                disabled={loadingTrendsMobData}
                key={tab.value}
                label={tab.label}
                disableRipple
                {...(tab.trackerId && value === index
                  ? { "data-tracker": tab.trackerId }
                  : {})}
                sx={{
                  color: colors.GrayDarkMode[700].$value,
                  textTransform: "none",
                  fontWeight: typography.Font_weight.Regular.$value,
                  minWidth: spacing.Spacing["7xl"].$value,
                  "&.Mui-selected": {
                    color: "#181D27",
                    fontWeight: typography.Font_weight.Medium.$value,
                  },
                  "&:hover": { backgroundColor: "transparent" },
                  "&:first-of-type": {
                    marginLeft: spacing.Spacing["6xl"].$value,
                  },
                }}
              />
            ))}
          </Tabs>
        </AppBar>

        <Box sx={{ px: 2, pt: 2 }}>
          <TrendsDetails />
          <TrendsMobileSortWrap />
        </Box>

        {/* TAB CONTENTS — only mounts after first visit, stays mounted with display:none */}
        {TAB_COMPONENTS.map((Component, index) => (
          <TabPanel
            key={tabConfig[index].value}
            activeIndex={value}
            index={index}
            visited={visitedTabs.has(index)}
          >
            <Component />
          </TabPanel>
        ))}

        {/* Create Tasks icon */}
        <Fab
          data-tracker="712"
          size="medium"
          color="secondary"
          aria-label="Create Tasks"
          onClick={() => setDrawerOpen(true)}
          sx={{
            backgroundColor: colors.Brand[600].$value,
            position: "fixed",
            bottom: spacing.Spacing["4xl"].$value,
            right: spacing.Spacing.xl.$value,
            "&:hover": { backgroundColor: colors.Brand[600].$value },
          }}
        >
          <AutoFixHighIcon />
        </Fab>
        <CreateTasksDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
        />
      </Box>
    </>
  );
}
